import React, { FC } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import {
  AccordionItemMediaFragmentFragment,
  ContentPlusMediaBlockMediaFragmentFragment,
  HeroBackgroundImageFragmentFragment,
  HeroMediaFragmentFragment,
  MediaBlockMediaFragmentFragment,
}
from "@graphqlTypes"

import { generateConditionalClassName } from "@utils"

import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"
import { Wistia } from "@helpscout/hs-marketing-shared-components"
import WistiaPopover from "@components/WistiaPopover/WistiaPopover"

import "./Media.scss"

type MediaFragment =
  AccordionItemMediaFragmentFragment &
  ContentPlusMediaBlockMediaFragmentFragment &
  HeroMediaFragmentFragment &
  HeroBackgroundImageFragmentFragment &
  MediaBlockMediaFragmentFragment

type MediaProps = MediaFragment & {
  className?: string
  excludeRoundedCorners?: boolean
  imageSrc?: string // if the image is static data
  innerRef?: React.Ref<HTMLElement | HTMLDivElement>
  isAboveFold?: boolean
  maxWidth?: number
}

const Media: FC<MediaProps> = ({
  className,
  excludeRoundedCorners,
  image,
  includeMediaBoxShadow,
  isAboveFold,
  innerRef,
  maxWidth = 1600,
  wistiaId,
  playVideoInline,
  playVideoInPnpPlayer,
}) => {
  const imageAlt = image?.description || image?.title
  const imageData = image?.imgixImage?.gatsbyImageData || image

  return (
    <div
      className={generateConditionalClassName({
        Media: true,
        "exclude-rounded-corners": !!excludeRoundedCorners,
        "include-box-shadow": !!includeMediaBoxShadow,
        "has-preview-image": wistiaId && playVideoInline && imageData,
      })}
      ref={innerRef as React.Ref<HTMLDivElement>}
    >
      {/* Wistia Popover */}
      {imageData && wistiaId && !playVideoInline && (
        <WistiaPopover
          id={wistiaId}
          imageAlt={imageAlt || "Click to view video"}
          imageData={imageData}
          maxWidth={maxWidth}
          usePnpPlayer={!!playVideoInPnpPlayer}
        />
      )}
      {/* Wistia Inline */}
      {wistiaId && playVideoInline && (
        <Wistia
          autoPlay
          id={wistiaId}
          muted
        />
      )}
      {/* Image Above Fold */}
      {!(wistiaId && !playVideoInline) && imageData && isAboveFold && (
        <ImageAboveFold
          alt={imageAlt || "Image"}
          aspectRatio={1}
          className={className}
          imageData={imageData}
        />
      )}
      {/* Default (GatsbyImage) */}
      {!(wistiaId && !playVideoInline) && imageData && !isAboveFold && (
        <GatsbyImage
          alt={imageAlt || "Image"}
          className="standalone-image"
          image={imageData}
        />
      )}
    </div>
  )
}

// Unfortunately, we have to create a fragment for each ContentfulLp block type
// because of the required verbosity of GraphQL
export const MediaFragments = graphql`
  fragment AccordionItemMediaFragment on ContentfulLpAccordionItem {
    image {
      description
      imgixImage {
        gatsbyImageData(width: 1600)
      }
      title
    }
    includeMediaBoxShadow
    playVideoInline
    playVideoInPnpPlayer
    wistiaId
  }

  fragment ContentPlusMediaBlockMediaFragment on ContentfulLpContentPlusMediaBlock {
    image {
      description
      imgixImage {
        gatsbyImageData(width: 1600)
      }
      title
    }
    includeMediaBoxShadow
    playVideoInline
    playVideoInPnpPlayer
    wistiaId
  }

  fragment HeroMediaFragment on ContentfulLpHero {
    image: media {
      description
      imgixImage {
        gatsbyImageData(width: 1600)
      }
      title
    }
    includeMediaBoxShadow
    playVideoInline
    playVideoInPnpPlayer
    wistiaId: wistiaVideoId
  }

  fragment HeroBackgroundImageFragment on ContentfulLpHero {
    backgroundImage {
      description
      imgixImage {
        gatsbyImageData(width: 822)
      }
      title
    }
  }

  fragment MediaBlockMediaFragment on ContentfulLpMediaBlock {
    image {
      description
      imgixImage {
        gatsbyImageData(width: 1600)
      }
      title
    }
    includeMediaBoxShadow
    playVideoInline
    playVideoInPnpPlayer
    wistiaId
  }
`

export default Media
